// @flow
import React from 'react';
import { withRouter } from 'react-router-dom';
import { components, elements } from '@peachjar/components';
import { Mutation } from 'react-apollo';
import styles from './SetupOrganizationContainer.css';
import FlyerApprovalGuidelines from '../components/FlyerApprovalGuidelines';
import OrganizationCommunication from '../components/OrganizationCommunication';
import OrganizationFlyerDisclaimer from '../components/OrganizationFlyerDisclaimer';
import OrganizationDenialContactInformation from '../components/OrganizationDenialContactInformation';
import StaffGroups from '../components/StaffGroups';
import VISIT_DISTRICT_SETUP_TASK_MUTATION from "../../_shared/gql/visitDistrictSetupTask.mutation";

const { NumberedRow, Tabber } = components;
const {
  Buttons: { ButtonPrimaryLarge },
  LoadingIndicators: { Spinner },
} = components;

const { typography } = elements;
const { Headline1 } = typography;

const PARENT_SETUP_ROUTE = '/onboarding/setup/parent';

const onContinueClick = (handleContinue, markTaskVisited, variables) => () => {
  handleContinue();
  // TODO: Push to parent setup route only on 200 response
  markTaskVisited({ variables });
};

export type ReduxDispatchProps = {|
  +handleFlyerApprovalGuidelinesUrlChange: (e: any) => void,
  handleStaffGroupNotificationRadioSelectionNo: () => void,
  handleStaffGroupNotificationRadioSelectionYes: () => void,
  handleFlyerDisclaimerRequiredRadioSelectionYes: () => void,
  handleFlyerDisclaimerRequiredRadioSelectionNo: () => void,
  handleFlyerDisclaimer: (e: any) => void,
  handleDenialContact: (e: any) => void,
  handleContinue: (e: any) => void,
  onNotifyError: (error: any) => void,
  handleOrganizationCommunicationDownloaded: () => void,
|};

export type ReduxStateProps = {|
  flyerApprovalGuidelinesUrl: string,
  isValidUrl: boolean,
  canSendFlyersToStaff: boolean,
  flyerDisclaimerRequired: boolean,
  flyerDisclaimer: string,
  organizationDenialContactInformation: string,
  organizationCommunicationDownloaded: boolean,
|};

type Props = {|
  history: any,
  ncesDistrictId: string,
  ...ReduxDispatchProps,
  ...ReduxStateProps,
|};

const generateTabs = () => {
  const tabs = [
    {
      id: 1,
      isActive: true,
      icon: 'Settings',
      label: 'Configure your settings',
      labelSubText: 'for the organization in your district.',
      onClickHandler: null,
    },
  ];
  return tabs;
};

const SetupOrganizationContainer = ({
  flyerApprovalGuidelinesUrl,
  handleFlyerApprovalGuidelinesUrlChange,
  isValidUrl,
  handleStaffGroupNotificationRadioSelectionYes,
  handleStaffGroupNotificationRadioSelectionNo,
  handleFlyerDisclaimerRequiredRadioSelectionYes,
  handleFlyerDisclaimerRequiredRadioSelectionNo,
  canSendFlyersToStaff,
  flyerDisclaimerRequired,
  handleFlyerDisclaimer,
  flyerDisclaimer,
  organizationDenialContactInformation,
  handleDenialContact,
  handleContinue,
  history,
  ncesDistrictId,
  onNotifyError,
  handleOrganizationCommunicationDownloaded,
  organizationCommunicationDownloaded,
  isOrgComplete,
}: Props) => {
  const isReadyToContinue = () => {
    if (flyerDisclaimerRequired) {
      return organizationCommunicationDownloaded && flyerDisclaimer.length > 0;
    } else {
      return organizationCommunicationDownloaded;
    }
  };

  return (
    <React.Fragment>
      <Headline1 style={{ margin: '1rem 0' }}>Organization Setup</Headline1>
      <div
        style={{
          backgroundColor: '#f5f5f5',
          flex: '1 1 auto',
        }}
      >
        <Tabber tabs={generateTabs()} />
        <div className={`container ${styles.container}`}>
          <div className={`row flex-column ${styles.numberedRowWrapper}`}>
            <NumberedRow
              className="numberedRow"
              rowNumber={1}
              headerText="Flyer Approval Guidelines"
              isOptional
              horizontalRule
            >
              <FlyerApprovalGuidelines
                handleFlyerApprovalGuidelinesUrlChange={
                  handleFlyerApprovalGuidelinesUrlChange
                }
                flyerApprovalGuidelinesUrl={flyerApprovalGuidelinesUrl}
                isValidUrl={isValidUrl}
              />
            </NumberedRow>
            <NumberedRow
              className="numberedRow"
              rowNumber={2}
              headerText="Organization Communication"
              horizontalRule
            >
              <OrganizationCommunication
                handleOrganizationCommunicationDownloaded={
                  handleOrganizationCommunicationDownloaded
                }
              />
            </NumberedRow>
            <NumberedRow
              className="numberedRow"
              rowNumber={3}
              headerText="Staff Groups"
              isOptional
              horizontalRule
            >
              <StaffGroups
                canSendFlyersToStaff={canSendFlyersToStaff}
                handleStaffGroupNotificationRadioSelectionYes={
                  handleStaffGroupNotificationRadioSelectionYes
                }
                handleStaffGroupNotificationRadioSelectionNo={
                  handleStaffGroupNotificationRadioSelectionNo
                }
              />
            </NumberedRow>
            <NumberedRow
              className="numberedRow"
              rowNumber={4}
              headerText="Organization Flyer Disclaimer"
              horizontalRule
              isOptional
            >
              <OrganizationFlyerDisclaimer
                flyerDisclaimerRequired={flyerDisclaimerRequired}
                handleFlyerDisclaimerRequiredRadioSelectionYes={
                  handleFlyerDisclaimerRequiredRadioSelectionYes
                }
                handleFlyerDisclaimerRequiredRadioSelectionNo={
                  handleFlyerDisclaimerRequiredRadioSelectionNo
                }
                handleFlyerDisclaimer={handleFlyerDisclaimer}
                flyerDisclaimer={flyerDisclaimer}
              />
            </NumberedRow>
            <NumberedRow
              className="numberedRow"
              rowNumber={5}
              headerText="Organization Denial Contact Information"
              isOptional
            >
              <OrganizationDenialContactInformation
                organizationDenialContactInformation={
                  organizationDenialContactInformation
                }
                handleDenialContact={handleDenialContact}
              />
            </NumberedRow>
          </div>

          <Mutation
            mutation={VISIT_DISTRICT_SETUP_TASK_MUTATION}
            onError={onNotifyError}
            onCompleted={() => history.push(PARENT_SETUP_ROUTE)}
          >
            {(markTaskVisited, { loading, error, data }) => {
              const variables = {
                input: {
                  ncesDistrictId,
                  taskName: 'orgsEmailed',
                },
              };

              return (
                <div className="row d-flex justify-content-end pr-5">
                  <ButtonPrimaryLarge
                    id="continue-sendOrgButton"
                    onClick={onContinueClick(
                      handleContinue,
                      markTaskVisited,
                      variables
                    )}
                    disabled={!isReadyToContinue() && !isOrgComplete}
                  >
                    {loading && <Spinner />}Continue to Parent Setup
                  </ButtonPrimaryLarge>
                </div>
              );
            }}
          </Mutation>
        </div>
      </div>
    </React.Fragment>
  );
};

export { SetupOrganizationContainer, PARENT_SETUP_ROUTE };

export default withRouter(SetupOrganizationContainer);



// WEBPACK FOOTER //
// ./src/Onboarding/Setup/organization/containers/SetupOrganizationContainer.jsx